import React, { useState, useEffect } from 'react';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';
import TaxSyncPreviewModal from './TaxSyncPreviewModal';
import TaxSyncHistoryModal from './TaxSyncHistoryModal';

export default function TaxApiConfig() {
  const addToast = useToast();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setAutoSyncEnabled(data.autoSyncEnabled || false);
      }
    } catch (err) {
      console.error('Failed to fetch tax API config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!apiKey && !hasChanges) {
      addToast('No changes to save', 'warning');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      
      const payload = {};
      if (apiKey) payload.apiKey = apiKey;
      payload.autoSyncEnabled = autoSyncEnabled;

      const response = await fetch(`${backendUrl}/api/admin/tax-api/config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        addToast('Tax API configuration saved successfully', 'success');
        setApiKey('');
        setHasChanges(false);
        await fetchConfig(); // Refetch to get updated state
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to save configuration', 'error');
      }
    } catch (err) {
      console.error('Failed to save config:', err);
      addToast('Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config?.apiKeyLast4 && !apiKey) {
      addToast('Please save an API key first', 'warning');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/test-connection`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = await response.json();
      
      if (result.success) {
        addToast(`✓ ${result.message}`, 'success');
        await fetchConfig(); // Refetch to get updated connection status
      } else {
        addToast(`✗ ${result.message}`, 'error');
        await fetchConfig(); // Refetch even on error to update status
      }
    } catch (err) {
      console.error('Connection test failed:', err);
      addToast('Connection test failed', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleCopyKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      addToast('API key copied to clipboard', 'success');
    }
  };

  if (loading) {
    return (
      <div style={S.card}>
        <p style={{ color: C.textMid }}>Loading configuration...</p>
      </div>
    );
  }

  const connectionBadge = () => {
    const status = config?.connectionStatus || 'not_configured';
    const badges = {
      connected: { bg: 'rgb(220, 252, 231)', color: 'rgb(22, 163, 74)', text: 'Connected', icon: 'checkmark-circle' },
      error: { bg: 'rgb(254, 226, 226)', color: 'rgb(220, 38, 38)', text: 'Error', icon: 'close-circle' },
      not_configured: { bg: 'rgb(241, 245, 249)', color: 'rgb(100, 116, 139)', text: 'Not Configured', icon: 'alert-circle' },
      not_tested: { bg: 'rgb(254, 243, 199)', color: 'rgb(217, 119, 6)', text: 'Not Tested', icon: 'warning' }
    };

    const badge = badges[status] || badges.not_configured;

    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 10,
        background: badge.bg,
        color: badge.color,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif"
      }}>
        <Icon name={badge.icon} style={{ fontSize: 16 }} />
        {badge.text}
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={S.heading}>Tax Data API Configuration</h2>
            <p style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>
              Integrate with PayrollTaxAPI.com to sync state and federal tax rates automatically
            </p>
          </div>
        </div>

        {/* Bento Grid: Config + Status */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          
          {/* API Configuration Card */}
          <div style={S.card}>
            <h3 style={{ ...S.subheading, marginBottom: 16 }}>API Configuration</h3>
            
            {/* API Key Input */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="tax-api-key-input" style={S.label}>
                PayrollTaxAPI API Key
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    id="tax-api-key-input"
                    name="taxApiKey"
                    data-testid="tax-api-key-input"
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder={config?.apiKeyLast4 ? `API key configured (ending in ${config.apiKeyLast4})` : 'ptx_free_...'}
                    style={{
                      ...S.input,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 13,
                      paddingRight: apiKey ? 80 : 12
                    }}
                  />
                  {apiKey && (
                    <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
                      <button
                        data-testid="tax-api-key-reveal-button"
                        onClick={() => setShowKey(!showKey)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: 'transparent',
                          color: C.textMid,
                          cursor: 'pointer',
                          borderRadius: 6
                        }}
                        title={showKey ? 'Hide' : 'Reveal'}
                      >
                        <Icon name={showKey ? 'eye-off-outline' : 'eye-outline'} style={{ fontSize: 16 }} />
                      </button>
                      <button
                        data-testid="tax-api-key-copy-button"
                        onClick={handleCopyKey}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: 'transparent',
                          color: C.textMid,
                          cursor: 'pointer',
                          borderRadius: 6
                        }}
                        title="Copy"
                      >
                        <Icon name="copy-outline" style={{ fontSize: 16 }} />
                      </button>
                    </div>
                  )}
                </div>
                <button
                  data-testid="tax-api-key-save-button"
                  onClick={handleSaveConfig}
                  disabled={saving || (!apiKey && !hasChanges)}
                  style={{
                    ...S.buttonPrimary,
                    minWidth: 80,
                    opacity: (saving || (!apiKey && !hasChanges)) ? 0.6 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              <p style={{ fontSize: 12, color: C.textMid, marginTop: 6 }}>
                {config?.apiKeyLast4 ? (
                  <>
                    API key configured. <strong>Paste a new key to replace it.</strong> For security, the full key cannot be displayed once saved.
                  </>
                ) : (
                  <>
                    Get your API key from <a href="https://payrolltaxapi.com/signup" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>PayrollTaxAPI.com</a>
                  </>
                )}
              </p>
            </div>

            {/* Auto-sync Toggle */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="tax-api-auto-sync-toggle" style={S.label}>
                Automated Monthly Sync
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <input
                  id="tax-api-auto-sync-toggle"
                  name="autoSyncEnabled"
                  data-testid="tax-api-auto-sync-toggle"
                  type="checkbox"
                  checked={autoSyncEnabled}
                  onChange={(e) => {
                    setAutoSyncEnabled(e.target.checked);
                    setHasChanges(true);
                  }}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: C.text }}>
                  Enable automatic sync on last day of month (2am ET)
                </span>
              </div>
              <p style={{ fontSize: 12, color: C.textMid, marginTop: 6 }}>
                All syncs are logged for audit. You'll be notified to review changes before they're applied.
              </p>
            </div>

            {/* Warning Note */}
            <div style={{
              padding: 12,
              borderRadius: 10,
              background: 'rgb(254, 243, 199)',
              border: '1px solid rgb(217, 119, 6)',
              fontSize: 12,
              color: 'rgb(120, 53, 15)',
              lineHeight: 1.5
            }}>
              <strong>⚠️ Compliance Note:</strong> Tax rate updates can affect payroll calculations. Always review changes carefully before applying.
            </div>
          </div>

          {/* Connection Status Card */}
          <div style={S.card}>
            <h3 style={{ ...S.subheading, marginBottom: 16 }}>Connection Status</h3>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: C.textMid, marginBottom: 8 }}>Current Status</div>
              {connectionBadge()}
            </div>

            {config?.lastConnectionTest && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: C.textMid, marginBottom: 4 }}>Last Test</div>
                <div style={{ fontSize: 13, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                  {new Date(config.lastConnectionTest).toLocaleString()}
                </div>
              </div>
            )}

            <button
              data-testid="tax-api-test-connection-button"
              onClick={handleTestConnection}
              disabled={testing || (!config?.apiKeyLast4 && !apiKey)}
              style={{
                ...S.buttonSecondary,
                width: '100%',
                opacity: (testing || (!config?.apiKeyLast4 && !apiKey)) ? 0.6 : 1
              }}
            >
              {testing ? (
                <>
                  <Icon name="sync-outline" style={{ fontSize: 16, animation: 'spin 1s linear infinite' }} />
                  Testing Connection...
                </>
              ) : (
                <>
                  <Icon name="flash-outline" style={{ fontSize: 16 }} />
                  Test Connection
                </>
              )}
            </button>

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <button
                data-testid="tax-sync-history-open-button"
                onClick={() => setShowHistoryModal(true)}
                style={{
                  ...S.buttonSecondary,
                  width: '100%',
                  marginBottom: 8
                }}
              >
                <Icon name="time-outline" style={{ fontSize: 16 }} />
                View Sync History
              </button>
            </div>
          </div>
        </div>

        {/* Sync Actions Card */}
        <div style={S.card}>
          <h3 style={{ ...S.subheading, marginBottom: 8 }}>Manual Sync</h3>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 16 }}>
            Fetch the latest tax rates from PayrollTaxAPI and preview changes before applying.
          </p>
          <button
            data-testid="tax-sync-preview-open-button"
            onClick={() => setShowPreviewModal(true)}
            disabled={!config?.apiKeyLast4}
            style={{
              ...S.buttonPrimary,
              opacity: !config?.apiKeyLast4 ? 0.6 : 1
            }}
          >
            <Icon name="cloud-download-outline" style={{ fontSize: 16 }} />
            Preview & Sync Latest Tax Rates
          </button>
          {!config?.apiKeyLast4 && (
            <p style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>
              Please configure and save an API key first
            </p>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPreviewModal && (
        <TaxSyncPreviewModal
          onClose={() => setShowPreviewModal(false)}
          onApplied={() => {
            setShowPreviewModal(false);
            addToast('Tax rates updated successfully', 'success');
          }}
        />
      )}

      {showHistoryModal && (
        <TaxSyncHistoryModal
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
